import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Fieldset } from 'primereact/fieldset';
import apiFetch from '../../auth/apiFetch';
import { properties } from '../../properties';

function isoToPtBr(iso) {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
}

function ptBrToIso(ptbr) {
    if (!ptbr) return '';
    const clean = ptbr.replace(/_/g, '');
    if (clean.length < 10) return '';
    const [d, m, y] = clean.split('/');
    if (!d || !m || !y || y.length < 4) return '';
    return `${y}-${m}-${d}`;
}

const emptyTerm = { id: null, description: '' };

export function BoardTermsList() {
    const [terms, setTerms] = useState([]);
    const [term, setTerm] = useState(emptyTerm);
    const [startDateDisplay, setStartDateDisplay] = useState('');
    const [endDateDisplay, setEndDateDisplay] = useState('');
    const [copyEnabled, setCopyEnabled] = useState(false);
    const [copyFromTerm, setCopyFromTerm] = useState(null);
    const [dialog, setDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [apiError, setApiError] = useState('');
    const toast = useRef(null);

    useEffect(() => { load(); }, []);

    async function load() {
        try {
            const res = await apiFetch(`${properties.baseURL}${properties.boardTermsPath}`);
            if (!res.ok) throw new Error(`Erro ${res.status} ao carregar gestões`);
            setTerms(await res.json());
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: e.message, life: 5000 });
        }
    }

    function openNew() {
        setTerm(emptyTerm);
        setStartDateDisplay('');
        setEndDateDisplay('');
        setCopyEnabled(false);
        setCopyFromTerm(null);
        setApiError('');
        setDialog(true);
    }

    function openEdit(row) {
        setTerm({ id: row.id, description: row.description });
        setStartDateDisplay(isoToPtBr(row.startDate));
        setEndDateDisplay(isoToPtBr(row.endDate));
        setCopyEnabled(false);
        setCopyFromTerm(null);
        setApiError('');
        setDialog(true);
    }

    function openDelete(row) {
        setTerm(row);
        setDeleteDialog(true);
    }

    async function save() {
        if (!term.description.trim()) {
            setApiError('A descrição é obrigatória.');
            return;
        }
        const startIso = ptBrToIso(startDateDisplay);
        if (!startIso) {
            setApiError('Data de início inválida. Use o formato DD/MM/AAAA.');
            return;
        }
        const endIso = endDateDisplay ? ptBrToIso(endDateDisplay) : null;
        if (endDateDisplay && !endIso) {
            setApiError('Data de encerramento inválida. Use o formato DD/MM/AAAA ou deixe em branco.');
            return;
        }

        setApiError('');
        const isEdit = !!term.id;
        const body = { description: term.description, startDate: startIso, endDate: endIso };
        if (!isEdit && copyEnabled && copyFromTerm) {
            body.copyFromTermId = copyFromTerm.id;
        }

        const url = isEdit
            ? `${properties.baseURL}${properties.boardTermsPath}/${term.id}`
            : `${properties.baseURL}${properties.boardTermsPath}`;
        try {
            const res = await apiFetch(url, {
                method: isEdit ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const resBody = await res.json().catch(() => ({}));
                if (res.status === 409) {
                    throw new Error(
                        'Já existe uma gestão vigente (sem data de encerramento). ' +
                        'Encerre a gestão atual antes de criar uma nova.'
                    );
                }
                throw new Error(resBody.error || `Erro ${res.status}`);
            }
            toast.current.show({
                severity: 'success', summary: 'Sucesso',
                detail: isEdit ? 'Gestão atualizada' : 'Gestão criada', life: 3000,
            });
            setDialog(false);
            load();
        } catch (e) {
            setApiError(e.message);
        }
    }

    async function deleteTerm() {
        setDeleteDialog(false);
        try {
            const res = await apiFetch(
                `${properties.baseURL}${properties.boardTermsPath}/${term.id}`,
                { method: 'DELETE' }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                const detail = res.status === 400
                    ? `A gestão "${term.description}" possui membros e não pode ser removida. Remova todos os membros antes.`
                    : (body.error || `Erro ${res.status}`);
                throw new Error(detail);
            }
            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Gestão removida', life: 3000 });
            load();
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Erro', detail: e.message, life: 6000 });
        }
    }

    function openComposition(row) {
        window.location.href = `/board/terms/${row.id}/composition`;
    }

    const statusBody = (row) =>
        !row.endDate ? <Tag severity="success" value="Vigente" /> : null;

    const startBody = (row) => isoToPtBr(row.startDate);

    const endBody = (row) =>
        row.endDate ? isoToPtBr(row.endDate) : <span className="text-400 text-sm">em andamento</span>;

    const actionBody = (row) => (
        <>
            <Button
                icon="pi pi-users"
                className="p-button-rounded p-button-info mr-2"
                onClick={() => openComposition(row)}
                tooltip="Ver composição"
            />
            <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-success mr-2"
                onClick={() => openEdit(row)}
                tooltip="Editar"
            />
            <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-danger"
                onClick={() => openDelete(row)}
                tooltip="Excluir"
            />
        </>
    );

    const rowClassName = (row) => ({ 'font-bold': !row.endDate });

    const dialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={() => setDialog(false)} />
            <Button label="Salvar" icon="pi pi-check" onClick={save} />
        </>
    );

    const deleteDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={() => setDeleteDialog(false)} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={deleteTerm} />
        </>
    );

    return (
        <div className="datatable-crud-demo surface-card p-4 border-round shadow-2">
            <Toast ref={toast} />

            <div className="flex justify-content-between align-items-center mb-4">
                <span className="text-3xl text-800 font-bold">Gestões</span>
                <Button icon="pi pi-plus" label="Nova gestão" className="p-button-rounded" onClick={openNew} />
            </div>

            <DataTable
                value={terms}
                dataKey="id"
                responsiveLayout="scroll"
                rowClassName={rowClassName}
                emptyMessage="Nenhuma gestão cadastrada."
            >
                <Column field="description" header="Descrição" sortable />
                <Column header="Início" body={startBody} style={{ width: '9rem' }} />
                <Column header="Encerramento" body={endBody} style={{ width: '10rem' }} />
                <Column header="Status" body={statusBody} style={{ width: '7rem' }} />
                <Column body={actionBody} exportable={false} style={{ width: '11rem' }} />
            </DataTable>

            {/* Dialog criar / editar */}
            <Dialog
                visible={dialog}
                style={{ width: '500px' }}
                header={term.id ? 'Editar gestão' : 'Nova gestão'}
                modal className="p-fluid"
                footer={dialogFooter}
                onHide={() => setDialog(false)}
            >
                {apiError && (
                    <Message severity="error" text={apiError} className="w-full mb-3"
                        style={{ justifyContent: 'flex-start' }} />
                )}
                <Fieldset>
                    <div className="field">
                        <label htmlFor="termDescription">Descrição</label>
                        <InputText
                            id="termDescription"
                            value={term.description}
                            onChange={e => setTerm({ ...term, description: e.target.value })}
                            placeholder="Ex.: Gestão 2024-2026"
                        />
                    </div>
                    <div className="formgrid grid">
                        <div className="field col-6">
                            <label>Data de início</label>
                            <InputMask
                                mask="99/99/9999"
                                value={startDateDisplay}
                                onChange={e => setStartDateDisplay(e.target.value)}
                                placeholder="01/01/2024"
                            />
                        </div>
                        <div className="field col-6">
                            <label>
                                Data de encerramento
                                <small className="text-400 ml-1">(vazio = vigente)</small>
                            </label>
                            <InputMask
                                mask="99/99/9999"
                                value={endDateDisplay}
                                onChange={e => setEndDateDisplay(e.target.value)}
                                placeholder="31/12/2026"
                            />
                        </div>
                    </div>
                    {/* Opção de copiar composição — só na criação */}
                    {!term.id && (
                        <div className="field">
                            <div className="flex align-items-center gap-2 mb-2">
                                <Checkbox
                                    inputId="copyChk"
                                    checked={copyEnabled}
                                    onChange={e => { setCopyEnabled(e.checked); if (!e.checked) setCopyFromTerm(null); }}
                                />
                                <label htmlFor="copyChk" className="cursor-pointer">
                                    Copiar composição de uma gestão anterior
                                </label>
                            </div>
                            {copyEnabled && (
                                <Dropdown
                                    value={copyFromTerm}
                                    options={terms}
                                    onChange={e => setCopyFromTerm(e.value)}
                                    optionLabel="description"
                                    placeholder="Selecione a gestão de origem"
                                    className="w-full"
                                />
                            )}
                        </div>
                    )}
                </Fieldset>
            </Dialog>

            {/* Dialog confirmar exclusão */}
            <Dialog
                visible={deleteDialog}
                style={{ width: '450px' }}
                header="Confirmar exclusão"
                modal
                footer={deleteDialogFooter}
                onHide={() => setDeleteDialog(false)}
            >
                <div className="flex align-items-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    <span>
                        Remover a gestão <b>{term.description}</b>?<br />
                        <small className="text-500">Não é possível remover se houver membros cadastrados.</small>
                    </span>
                </div>
            </Dialog>
        </div>
    );
}
